import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetDataParam} from 'features/core/shared/widget/widget-data-param';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {WidgetGenericEvent} from 'features/core/shared/widget/event/widget-generic-event';
import {
    MvsWidgetFormGenericEventConstants
} from 'features/core/shared/widget/component/runtime/implementations/mvs-widget-form/mvs-widget-form-generic-event.constants';
import {MvsFormGroup} from 'features/core/shared/logic/form/mvs-form-group';
import {MvsFormDto} from 'features/core/shared/dto/form/dto/mvs-form.dto';
import {MvsFormFieldDto} from 'features/core/shared/dto/form/dto/mvs-form-field.dto';
import {
    MvsFormValueListEntryDto
} from 'features/core/shared/dto/form/dto/mvs-form-value-list-entry.dto';
import {MvsCoreService} from 'features/core/shared/service/mvs-core.service';
import {MvsObjectService} from 'features/core/shared/service/object/mvs-object.service';
import {MvsMessageService} from 'features/core/shared/service/message/mvs-message.service';
import {
    ObjectChangeInformation,
    ObjectChangeInformationActionEnum
} from 'features/core/shared/logic/object-change-information';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {ObjectRequestList} from 'features/core/shared/dto/object/request/object-request-list';
import {CpAgentTransactionService} from '../../../../../service/api/cp-agent-transaction.service';
import {CpFeatureRight} from '../../../../../service/domain/cp-feature-right';
import {
    ProvisionTransferDirection,
    ProvisionTransferRequestDto,
    ProvisionTransferResultDto
} from '../../../../../model/dto/api/provision-transfer.dto';
import {ProvisionAgentBalanceDto} from '../../../../../model/dto/entity/provision-agent-balance.dto';

/**
 * Action dialog "Transfer" (German UI label "Umbuchung") —
 * `POST /mvsa/cp/provisionAgentTransactions/transfer`
 * (03-architektur-backend.md §11.1, U-03/KF-08; um right `cp_reserveTransfer`).
 *
 * The transfer is an ATOMIC PAIR: two equal-amount ACTIVE bookings (event
 * RESERVE_TRANSFER) in one transaction. Both directions are allowed and the direction is a
 * MANDATORY FIELD of the dialog:
 *   RESERVE_TO_PROVISION — reserve account -> provision account
 *   PROVISION_TO_RESERVE — provision account -> reserve account
 * The reason (`referenceText`) is likewise a MANDATORY FIELD.
 *
 * ────────────────────────────────────────────────────────────────────────────────────────────
 * FORM MECHANISM USED (design gate finding B-9 — MANDATORY DOCUMENTATION):
 * TRANSIENT FORM WIDGET built from an `MvsFormDto` constructed in the frontend, identical to
 * the sibling dialog {@link CpProvisionAgentManualBookingDialogComponent}:
 * `WidgetFactory.createWidget(alias, name, 'form', 'transient', 'transient', objectType,
 *  WidgetDataParam.create('form', form))` -> `<mvs-widget (onGenericEvent)="…">`.
 *
 * Here this mechanism is not merely convenient but the ONLY fitting one: the transfer has
 * NO own entity. `direction` is a pure request field (it exists in no table),
 * so neither `openObjectViaDialog(...)` nor an `mvs-crud-object` on an entity alias can
 * produce this form — both paths presuppose a backend FormObject of an entity. The
 * transient form widget is the repo-documented path for exactly this case.
 * REPO EVIDENCE: `features/feature-core/wf/component/protected-components/wf-process-start/
 * wf-process-start.component.ts` — start parameters of a process, likewise without an entity.
 * There is no hand-written form HTML in this dialog.
 * ────────────────────────────────────────────────────────────────────────────────────────────
 *
 * BALANCE PREVIEW: both accounts are shown simultaneously — source account minus amount, target
 * account plus amount. If this would push the reserve account negative, the underfunding hint
 * appears (U-03, payout brake). The binding coverage check is done by the backend; a business
 * failure comes back as HTTP 200 with `success = false`.
 */
@Component({
    selector: 'cp-provision-agent-transfer-dialog',
    templateUrl: './cp-provision-agent-transfer-dialog.component.html',
    styleUrls: ['./cp-provision-agent-transfer-dialog.component.scss'],
    standalone: false
})
export class CpProvisionAgentTransferDialogComponent implements OnInit, OnChanges, OnDestroy {

    busy: boolean;
    initialized: boolean;

    @Input() provisionAgentId: number;

    /** Visibility of the dialog; intended as a two-way binding (`[(visible)]`). */
    @Input() visible: boolean = false;
    @Output() visibleChange = new EventEmitter<boolean>();

    /** Fires after a successfully written transfer. */
    @Output() onTransferred = new EventEmitter<ProvisionTransferResultDto>();

    /**
     * Fires when the server refused the um right `cp_reserveTransfer` with a 403.
     *
     * The demotion half of the module-wide OPTIMISTIC rights pattern (orchestrator decision OD-1,
     * reasoning in {@link CpFeatureRight}): the right cannot be known before acting — this
     * frontend has no feature-rights service and no cp payload carries a grant — so the action
     * starts open and a genuine refusal by `cp/cross/um/ProvisionFeatureGuard` demotes it. The
     * hosting pane listens and locks its entry button, so the user is not sent back into a dialog
     * the server will refuse again. Deliberate deviation from 04-frontend.md §5.1.
     */
    @Output() onForbidden = new EventEmitter<void>();

    /** The transient capture widget (see class Javadoc). */
    formWidget: WidgetData;

    /** Last valid form value; null as long as the form is incomplete/invalid. */
    formValue: any = null;

    /** Current balance of the agent — basis of the preview. */
    balanceDto: ProvisionAgentBalanceDto;

    /** Backend error message when `success = false`. */
    failureMessage: string = null;

    /** Both directions, exactly the values of the request DTO (U-03/KF-08). */
    static readonly DIRECTION_RESERVE_TO_PROVISION: ProvisionTransferDirection = 'RESERVE_TO_PROVISION';
    static readonly DIRECTION_PROVISION_TO_RESERVE: ProvisionTransferDirection = 'PROVISION_TO_RESERVE';

    constructor(
        protected coreService: MvsCoreService,
        protected mvsObjectService: MvsObjectService,
        protected messageService: MvsMessageService,
        protected agentTransactionService: CpAgentTransactionService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    initComponent(): void {
        this.initialized = true;
        if (this.visible) {
            this.refreshComponent();
        }
    }

    /** Rebuilds the form and the preview data basis fresh on every open. */
    refreshComponent(): void {

        this.formValue = null;
        this.failureMessage = null;
        this.formWidget = null;

        if (!this.provisionAgentId) {
            return;
        }

        this.busy = true;
        this.loadBalance();
        this.buildFormWidget();
        this.busy = false;
    }

    /** Loads the agent's current balance (1:1 row via the registry alias). */
    private loadBalance(): void {

        const objectRequestList = ObjectRequestList.createBasic(
            false,
            [FilterCriteria.create('provisionAgent', FilterCriteria.cOperatorEqual, this.provisionAgentId)],
            null);

        this.coreService.getCrudService('cp.ProvisionAgentBalance')
            .list(objectRequestList)
            .subscribe({
                next: (dtoList) => {
                    const entries = dtoList?.entries as ProvisionAgentBalanceDto[];
                    this.balanceDto = entries && entries.length ? entries[0] : null;
                },
                error: () => {
                    this.balanceDto = null;
                }
            });
    }

    /**
     * Builds the `MvsFormDto` of the transfer and the transient form widget from it.
     * Field ids match the attributes of {@link ProvisionTransferRequestDto} 1:1.
     */
    private buildFormWidget(): void {

        const form = new MvsFormDto();
        const formFields: MvsFormFieldDto[] = [];

        // Direction — MANDATORY FIELD, both directions allowed.
        const directionField = MvsFormFieldDto.createBasic(
            'direction', 'selectButton', 'Richtung der Umbuchung');
        directionField.uiMandatory = true;
        directionField.order = 10;
        directionField.createValueList(
            MvsFormValueListEntryDto.createBasic(
                CpProvisionAgentTransferDialogComponent.DIRECTION_RESERVE_TO_PROVISION,
                'Rücklage → Provision'),
            MvsFormValueListEntryDto.createBasic(
                CpProvisionAgentTransferDialogComponent.DIRECTION_PROVISION_TO_RESERVE,
                'Provision → Rücklage'));
        formFields['direction'] = directionField;

        // Amount — mandatory, positive.
        const amountField = MvsFormFieldDto.createBasic('amount', 'amount', 'Betrag');
        amountField.uiMandatory = true;
        amountField.uiDoubleMinValue = 0.01;
        amountField.order = 20;
        formFields['amount'] = amountField;

        // Valuta date — mandatory.
        const valutaDateField = MvsFormFieldDto.createBasic('valutaDate', 'date', 'Valutadatum');
        valutaDateField.uiMandatory = true;
        valutaDateField.order = 30;
        formFields['valutaDate'] = valutaDateField;

        // Reason — MANDATORY FIELD (03 §11.1 "mandatory reason").
        const referenceTextField = MvsFormFieldDto.createBasic('referenceText', 'textarea', 'Grund');
        referenceTextField.uiMandatory = true;
        referenceTextField.uiDescription = 'Pflichtangabe: warum wird umgebucht?';
        referenceTextField.order = 40;
        formFields['referenceText'] = referenceTextField;

        form.formFields = formFields;

        /**
         * AI: Transient form widget for capturing a transfer between the provision and
         *     reserve accounts.
         *     Source: MvsFormDto built in the frontend (the transfer has no own entity) —
         *     the write goes to POST /cp/provisionAgentTransactions/transfer.
         *     Caseworkers use it to move amounts between the agent's two accounts.
         */
        this.formWidget = WidgetFactory.createWidget(
            'cp.agent.transfer.form',
            'Umbuchung',
            'form',
            'transient',
            'transient',
            'cp.ProvisionAgentTransaction',
            WidgetDataParam.create('size', 'S'),
            WidgetDataParam.create('form', form)
        );
    }

    /** Receives the form values; only a fully valid form unlocks the action. */
    handleGenericEvent(event: WidgetGenericEvent): void {

        if (event.eventName !== MvsWidgetFormGenericEventConstants.EVENT_FORM_VALUE_CHANGE) {
            return;
        }

        const formGroup: MvsFormGroup =
            event.object[MvsWidgetFormGenericEventConstants.EVENT_FORM_VALUE_CHANGE_ATTR_FORM_GROUP];

        this.formValue = formGroup.status === 'VALID' ? formGroup.value : null;
        this.failureMessage = null;
    }

    /** Selected direction; null as long as nothing is selected. */
    get selectedDirection(): ProvisionTransferDirection {
        return (this.formValue?.['direction'] as ProvisionTransferDirection) ?? null;
    }

    /** Entered amount; null as long as no valid amount is present. */
    get enteredAmount(): number {
        const amount = Number(this.formValue?.['amount']);
        return !amount || isNaN(amount) ? null : amount;
    }

    /** Provision account AFTER the transfer. */
    get previewProvisionAfter(): number {
        const amount = this.enteredAmount;
        const before = this.balanceDto?.provisionBalance;

        if (amount == null || before == null || !this.selectedDirection) {
            return null;
        }
        return this.selectedDirection === CpProvisionAgentTransferDialogComponent.DIRECTION_RESERVE_TO_PROVISION
            ? before + amount
            : before - amount;
    }

    /** Reserve account AFTER the transfer. */
    get previewReserveAfter(): number {
        const amount = this.enteredAmount;
        const before = this.balanceDto?.reserveBalance;

        if (amount == null || before == null || !this.selectedDirection) {
            return null;
        }
        return this.selectedDirection === CpProvisionAgentTransferDialogComponent.DIRECTION_RESERVE_TO_PROVISION
            ? before - amount
            : before + amount;
    }

    /** True if the transfer would push the reserve account into the negative (U-03). */
    get previewCausesUnderfunding(): boolean {
        return this.previewReserveAfter != null && this.previewReserveAfter < 0;
    }

    /** The action button is only active with a fully valid form. */
    get submittable(): boolean {
        return !!this.formValue && !!this.selectedDirection && !this.busy;
    }

    /**
     * Writes the transfer to the custom endpoint. `success` is checked on EVERY response —
     * the coverage check can reject the debit side even though the request was well-formed.
     */
    handleTransfer(): void {

        if (!this.submittable) {
            return;
        }

        const request: ProvisionTransferRequestDto = {
            provisionAgentId: this.provisionAgentId,
            amount: Number(this.formValue['amount']),
            valutaDate: this.toIsoDate(this.formValue['valutaDate']),
            direction: this.selectedDirection,
            referenceText: this.formValue['referenceText']
        };

        this.busy = true;
        this.failureMessage = null;

        this.agentTransactionService.transfer(request).subscribe({
            next: (result: ProvisionTransferResultDto) => {
                this.busy = false;

                if (!result || result.success !== true) {
                    this.failureMessage = result?.failureMessage
                        ?? 'Die Umbuchung konnte nicht ausgeführt werden.';
                    return;
                }

                this.messageService.showSuccessMessage(
                    'Umbuchung', 'Die Umbuchung wurde ausgeführt.');

                this.informObjectChanged();
                this.onTransferred.emit(result);
                this.handleClose();
            },
            error: (error) => {
                this.busy = false;

                // A 403 is the um right answering; anything else is a technical/validation
                // failure and must NOT demote the right (see onForbidden).
                if (CpFeatureRight.isForbidden(error)) {
                    this.failureMessage =
                        CpFeatureRight.deniedMessage(CpFeatureRight.RESERVE_TRANSFER);
                    this.onForbidden.emit();
                    return;
                }

                this.failureMessage =
                    'Die Umbuchung konnte nicht ausgeführt werden. Bitte Eingaben prüfen.';
            }
        });
    }

    /** Informs the balance tile and the booking table about the new booking pair. */
    private informObjectChanged(): void {
        this.mvsObjectService.objectChangeEvent.next(
            ObjectChangeInformation.create(
                'cp.ProvisionAgentTransaction',
                ObjectChangeInformationActionEnum.created,
                null,
                null));
    }

    /** Date -> 'yyyy-MM-dd' (backend's LocalDate format). */
    private toIsoDate(value: any): string {
        if (!value) {
            return null;
        }
        const date = value instanceof Date ? value : new Date(value);
        const month = `${date.getMonth() + 1}`.padStart(2, '0');
        const day = `${date.getDate()}`.padStart(2, '0');
        return `${date.getFullYear()}-${month}-${day}`;
    }

    handleShow(): void {
        this.refreshComponent();
    }

    handleClose(): void {
        this.visible = false;
        this.visibleChange.emit(false);
    }

    handleVisibleChange(value: boolean): void {
        this.visible = value;
        this.visibleChange.emit(value);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['visible'] && this.visible) {
            this.refreshComponent();
        }
        if (changes['provisionAgentId'] && this.visible) {
            this.refreshComponent();
        }
    }

    ngOnDestroy(): void {
    }

}
